/**
 * Configuración de gamificación de SPACE (Duolingo-like) sobre `gamification-core`.
 *
 * El kit es AGNÓSTICO: aquí Space aporta SUS reglas (XP por completar lecciones,
 * racha de estudio diaria, badges, quests). Todo es puro → se calcula local-first
 * en el cliente para feedback inmediato y el servidor lo re-verifica (grading.rs).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "../gamification/badges";
import type { GamificationConfig } from "../gamification/engine";

/** Tipos de evento de dominio de Space. */
export const SPACE_EVENTS = {
  lessonCompleted: "lesson.completed",
  lessonPerfect: "lesson.perfect",
  courseCompleted: "course.completed",
} as const;

/** Bonus por lección perfecta (score = 1). */
export const PERFECT_BONUS_XP = 5;
/** XP por completar un curso entero. */
export const COURSE_COMPLETE_XP = 50;
/** XP base si una lección no declara `xp_reward`. */
export const DEFAULT_LESSON_XP = 10;
/** Meta de la quest diaria: lecciones por día. */
export const DAILY_LESSON_GOAL = 3;

/**
 * Config de Space. La XP de una lección viaja en `event.value` (el `xp_reward`
 * autoritativo de la lección); las perfectas y los cursos completados suman.
 */
export const spaceGamificationConfig: GamificationConfig = {
  xp: {
    [SPACE_EVENTS.lessonCompleted]: (e) => e.value ?? DEFAULT_LESSON_XP,
    [SPACE_EVENTS.lessonPerfect]: PERFECT_BONUS_XP,
    [SPACE_EVENTS.courseCompleted]: COURSE_COMPLETE_XP,
  },
  // Estudiar (completar cualquier lección) mantiene la racha "study".
  streakLines: {
    [SPACE_EVENTS.lessonCompleted]: ["study"],
  },
  badges: [
    {
      id: "first-lesson",
      title: "Primera lección",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 1),
    },
    {
      id: "ten-lessons",
      title: "10 lecciones",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 10),
    },
    {
      id: "hundred-lessons",
      title: "100 lecciones",
      when: counterAtLeast(SPACE_EVENTS.lessonCompleted, 100),
      bonusXp: 100,
    },
    { id: "streak-7", title: "Racha de 7 días", when: streakAtLeast("study", 7), bonusXp: 20 },
    { id: "streak-30", title: "Racha de 30 días", when: streakAtLeast("study", 30), bonusXp: 100 },
    {
      id: "first-course",
      title: "Primer curso completado",
      when: counterAtLeast(SPACE_EVENTS.courseCompleted, 1),
    },
    { id: "scholar", title: "Estudiante (nivel 5)", when: levelAtLeast(5) },
    { id: "sage", title: "Sabio (nivel 10)", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-lessons",
      title: "3 lecciones hoy",
      period: "daily",
      goals: [{ metric: SPACE_EVENTS.lessonCompleted, target: DAILY_LESSON_GOAL }],
      bonusXp: 15,
    },
  ],
};
